import * as THREE from "three";

/**
 * Convert MJCF mesh reference transform (refpos/refquat) into a transform suitable for
 * applying to raw mesh vertices in Three.js.
 *
 * In MJCF, `refpos/refquat` define the reference frame of the mesh asset.
 * Raw vertices are transformed into that reference frame as:
 *   v' = inverse(refquat) * (v - refpos)
 *
 * Three.js groups apply translation after rotation, so the equivalent group transform is:
 * - rotation: inverse(refquat)
 * - translation: -(inverse(refquat) * refpos)
 *
 * The returned quaternion is a Three.js quaternion (x,y,z,w).
 */
export function getVertexTransformFromMjcfMeshRef(meshDef) {
  const refPosArr = meshDef?.referencePosition;
  const refQuatArr = meshDef?.referenceOrientation;

  const refPosition = new THREE.Vector3(0, 0, 0);
  if (Array.isArray(refPosArr) && refPosArr.length === 3) {
    refPosition.set(
      Number(refPosArr[0]) || 0,
      Number(refPosArr[1]) || 0,
      Number(refPosArr[2]) || 0
    );
  }

  // MJCF quaternions are [w, x, y, z]. Three is (x, y, z, w).
  let quat = new THREE.Quaternion(0, 0, 0, 1);
  if (Array.isArray(refQuatArr) && refQuatArr.length === 4) {
    quat = new THREE.Quaternion(
      refQuatArr[1] ?? 0,
      refQuatArr[2] ?? 0,
      refQuatArr[3] ?? 0,
      refQuatArr[0] ?? 1
    ).normalize();
    // Convert "reference frame orientation" -> "vertex rotation"
    quat.invert();
  } else if (
    refQuatArr &&
    (refQuatArr.isQuaternion ||
      (typeof refQuatArr === "object" &&
        "x" in refQuatArr &&
        "y" in refQuatArr &&
        "z" in refQuatArr &&
        "w" in refQuatArr))
  ) {
    quat = new THREE.Quaternion(
      refQuatArr.x ?? 0,
      refQuatArr.y ?? 0,
      refQuatArr.z ?? 0,
      refQuatArr.w ?? 1
    )
      .normalize()
      .invert();
  }

  const position = refPosition.clone().applyQuaternion(quat).multiplyScalar(-1);

  return { position, quat };
}
